const partitions = {
  power_distribution: 1,
  other: 7,
  ground: 8,
  noEdge: 10,
}

export default function calculatePartition(compound) {
  if (compound.children().connectedEdges().length === 0) {
    return partitions.noEdge
  } else if (partitions[compound.data('category')]) {
    return partitions[compound.data('category')]
  }

  return partitions.other
}

export function getDefaultPartitions(graph) {
  const parents = graph.nodes().filter((ele) => ele.isParent())

  const partitions = {}
  parents.forEach((parent, index) => {
    partitions[parent.id()] = index
  })

  return partitions
}

/**
 * Assigns a numeric partition value to every component (parent/compound node) in the graph,
 * representing its BFS hop distance from the root component. These values serve two roles:
 * 1. ELK layer constraints — via nodeLayoutOptions, tells ELK which layer to place each
 *    component in (root component = layer 0 at top, deeper nodes = higher layers below)
 * 2. Port side decisions — getNonJumperPortSide compares two partition values to decide
 *    if a port faces NORTH or SOUTH
 *
 * @param {cytoscape.Core} graph
 * @param {string} componentId - Graph element ID or data.componentId of the root component
 * @returns {Record<string, number>} Map of parent node ID → partition (layer) index
 */
export function getComponentPartitions(graph, componentId) {
  const byId = graph.getElementById(componentId)
  const rootNode = byId.isParent()
    ? byId
    : graph
        .nodes()
        .filter((n) => n.isChild() && n.data('componentId') === componentId)
        .first()
        .parent()

  if (!rootNode || rootNode.empty()) return getDefaultPartitions(graph)

  const startId = rootNode.id()
  const partitions = {}
  const visited = new Set([startId])
  const queue = [{ id: startId, distance: 0 }]

  while (queue.length > 0) {
    const { id, distance } = queue.shift()
    partitions[id] = distance

    graph
      .getElementById(id)
      .children()
      .forEach((port) => {
        port
          .neighborhood()
          .nodes()
          .forEach((neighbor) => {
            const parentId = neighbor.isOrphan()
              ? neighbor.id()
              : neighbor.data('parent')
            if (parentId && !visited.has(parentId)) {
              visited.add(parentId)
              queue.push({ id: parentId, distance: distance + 1 })
            }
          })
      })
  }

  return partitions
}

export function getDetailedPartitions(graph, first, last) {
  const root = graph.getElementById(first).children()
  const goal = graph.getElementById(last).children()

  const permutations = root.flatMap((r) =>
    goal.map((g) => ({ root: r, goal: g })),
  )

  const paths = permutations.reduce((acc, permutation) => {
    const path = graph.elements().aStar({
      root: permutation.root,
      goal: permutation.goal,
    })

    if (path.found) {
      acc.push(path.path)
    }

    return acc
  }, [])

  const partitions = paths.reduce((acc, path) => {
    for (const [index, element] of path.entries()) {
      if (element.isNode()) {
        acc[element.data('parent')] = index
      }
    }
    return acc
  }, {})

  return partitions
}
